import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Buildings, CurrencyDollar, TrendUp, ChartLineUp } from '@phosphor-icons/react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '\u20AC' },
  { code: 'GBP', symbol: '\u00A3' },
  { code: 'PLN', symbol: 'z\u0142' }
];

const PROPERTY_TYPES = [
  { id: 'residential', label: 'Residential' },
  { id: 'commercial', label: 'Commercial' }
];

const COLORS = {
  primary: '#FF6B35',
  secondary: '#F7931E',
  danger: '#ef4444',
  muted: '#94a3b8'
};

const PublicRealEstateCalculator = () => {
  const [currency, setCurrency] = useState('USD');
  const [propertyType, setPropertyType] = useState('residential');
  const [formData, setFormData] = useState({
    propertySize: 100,
    pricePerSqm: 3000,
    downPaymentPercent: 20,
    interestRate: 4.5,
    mortgageTermYears: 25,
    rentCalculationType: 'per_sqm',
    rentValue: 15,
    taxRate: 20
  });
  const [results, setResults] = useState(null);

  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '$';

  const calculate = () => {
    const propertySize = parseFloat(formData.propertySize);
    const pricePerSqm = parseFloat(formData.pricePerSqm);
    const downPaymentPercent = parseFloat(formData.downPaymentPercent);
    const interestRate = parseFloat(formData.interestRate);
    const mortgageTermYears = parseInt(formData.mortgageTermYears);
    const rentValue = parseFloat(formData.rentValue);
    const taxRate = parseFloat(formData.taxRate);

    if ([propertySize, pricePerSqm, downPaymentPercent, interestRate, mortgageTermYears, rentValue, taxRate].some(isNaN)) return;

    const totalPropertyPrice = propertySize * pricePerSqm;
    const downPaymentAmount = totalPropertyPrice * (downPaymentPercent / 100);
    const loanAmount = totalPropertyPrice - downPaymentAmount;
    const monthlyInterestRate = interestRate / 100 / 12;
    const numPayments = mortgageTermYears * 12;

    let monthlyMortgagePayment = 0;
    if (monthlyInterestRate > 0 && numPayments > 0) {
      const pt = Math.pow(1 + monthlyInterestRate, numPayments);
      monthlyMortgagePayment = loanAmount * (monthlyInterestRate * pt) / (pt - 1);
    } else if (numPayments > 0) {
      monthlyMortgagePayment = loanAmount / numPayments;
    }

    const monthlyRentIncome = formData.rentCalculationType === 'per_sqm' ? rentValue * propertySize : rentValue;
    const monthlyTaxOnRent = monthlyRentIncome * (taxRate / 100);
    const monthlyNetIncome = monthlyRentIncome - monthlyTaxOnRent;
    const monthlyCashFlow = monthlyNetIncome - monthlyMortgagePayment;
    const totalAmountPaid = monthlyMortgagePayment * numPayments;
    const totalInterestPaid = totalAmountPaid - loanAmount;

    const yearsToCoverMortgage = monthlyCashFlow > 0 ? loanAmount / (monthlyCashFlow * 12) : null;
    const breakEvenYears = monthlyNetIncome > 0 ? totalPropertyPrice / (monthlyNetIncome * 12) : null;

    let roiPercentage = 0;
    if (yearsToCoverMortgage && yearsToCoverMortgage > 0 && downPaymentAmount > 0) {
      const totalRentalIncome = monthlyNetIncome * 12 * mortgageTermYears;
      const totalReturn = totalRentalIncome + totalPropertyPrice - totalPropertyPrice;
      roiPercentage = (totalReturn / downPaymentAmount) * 100;
    }

    const cashFlowTimeline = [];
    for (let year = 1; year <= Math.min(mortgageTermYears, 30); year++) {
      cashFlowTimeline.push({
        year,
        rent: Math.round(monthlyRentIncome * 12),
        mortgage: Math.round(monthlyMortgagePayment * 12),
        net: Math.round(monthlyCashFlow * 12)
      });
    }

    setResults({
      total_property_price: totalPropertyPrice,
      down_payment_amount: downPaymentAmount,
      loan_amount: loanAmount,
      monthly_mortgage_payment: monthlyMortgagePayment,
      monthly_rent_income: monthlyRentIncome,
      monthly_tax_on_rent: monthlyTaxOnRent,
      monthly_net_income: monthlyNetIncome,
      monthly_cash_flow: monthlyCashFlow,
      years_to_cover_mortgage: yearsToCoverMortgage,
      total_interest_paid: totalInterestPaid,
      total_amount_paid: totalAmountPaid,
      break_even_years: breakEvenYears,
      roi_percentage: roiPercentage,
      cash_flow_timeline: cashFlowTimeline
    });
  };

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const paymentBreakdownData = results ? [
    { name: 'Principal', value: results.loan_amount },
    { name: 'Interest', value: results.total_interest_paid },
    { name: 'Taxes', value: results.monthly_tax_on_rent * formData.mortgageTermYears * 12 }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1437] via-[#1a2556] to-[#0B1437]">
      <header className="bg-[#0B1437]/80 backdrop-blur-xl px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_realestateanalytics/artifacts/lhvv6y36_Menelaus%20Holding.png"
              alt="Menelaus Holding"
              className="w-10 h-10"
            />
            <span className="text-white font-semibold text-sm hidden sm:block">Menelaus Holding</span>
          </Link>
        </div>
      </header>

      <div className="px-4 sm:px-6 py-6 bg-[#0B1437]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-semibold text-white mb-1" data-testid="public-calc-title">Real Estate Analytics</h1>
          <p className="text-sm text-slate-400">Investment Calculator</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8"
        >
          <div>
            <label className="text-xs font-medium uppercase text-slate-400 mb-3 block tracking-wider">Property Type</label>
            <div className="flex gap-3">
              {PROPERTY_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setPropertyType(type.id)}
                  className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    propertyType === type.id
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF6B35]/80 text-white shadow-lg shadow-[#FF6B35]/20'
                      : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                  }`}
                  data-testid={`public-property-type-${type.id}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase text-slate-400 mb-3 block tracking-wider">Currency</label>
            <div className="grid grid-cols-4 gap-2">
              {CURRENCIES.map((curr) => (
                <button
                  key={curr.code}
                  onClick={() => setCurrency(curr.code)}
                  className={`px-3 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                    currency === curr.code
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF6B35]/80 text-white shadow-lg shadow-[#FF6B35]/20'
                      : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'
                  }`}
                  data-testid={`public-currency-${curr.code}`}
                >
                  {curr.code}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="lg:col-span-4 space-y-4 sm:space-y-6">
            <div className="card-premium rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 sm:mb-5">
                <Buildings size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Property Details</h3>
              </div>
              <div className="space-y-4">
                <InputField label="Size (sq m)" value={formData.propertySize} onChange={(e) => handleInputChange('propertySize', e.target.value)} testId="public-input-size" />
                <InputField label={`Price (${currencySymbol}/sq m)`} value={formData.pricePerSqm} onChange={(e) => handleInputChange('pricePerSqm', e.target.value)} testId="public-input-price" />
              </div>
            </div>
            <div className="card-premium rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 sm:mb-5">
                <CurrencyDollar size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Mortgage Details</h3>
              </div>
              <div className="space-y-4">
                <InputField label="Down Payment (%)" value={formData.downPaymentPercent} onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)} testId="public-input-dp" />
                <InputField label="Interest Rate (%)" value={formData.interestRate} onChange={(e) => handleInputChange('interestRate', e.target.value)} step="0.1" testId="public-input-rate" />
                <InputField label="Term (years)" value={formData.mortgageTermYears} onChange={(e) => handleInputChange('mortgageTermYears', e.target.value)} testId="public-input-term" />
              </div>
            </div>
            <div className="card-premium rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 sm:mb-5">
                <TrendUp size={20} weight="duotone" className="text-[#FF6B35]" />
                <h3 className="text-base font-semibold text-white">Rental Income & Tax</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium uppercase text-slate-400 mb-2 block tracking-wider">Calculation Type</label>
                  <div className="flex gap-2">
                    <button onClick={() => handleInputChange('rentCalculationType', 'per_sqm')} className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ${formData.rentCalculationType === 'per_sqm' ? 'bg-[#FF6B35] text-white shadow-md' : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'}`} data-testid="public-rent-per-sqm">Per sq m</button>
                    <button onClick={() => handleInputChange('rentCalculationType', 'total')} className={`flex-1 px-3 py-2 rounded-full text-xs font-medium transition-all duration-200 ${formData.rentCalculationType === 'total' ? 'bg-[#FF6B35] text-white shadow-md' : 'bg-transparent border border-white/10 text-slate-300 hover:border-white/20'}`} data-testid="public-rent-total">Total</button>
                  </div>
                </div>
                <InputField label="Rent Value" value={formData.rentValue} onChange={(e) => handleInputChange('rentValue', e.target.value)} testId="public-input-rent" />
                <InputField label="Tax Rate (%)" value={formData.taxRate} onChange={(e) => handleInputChange('taxRate', e.target.value)} testId="public-input-tax" />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="lg:col-span-8 space-y-4 sm:space-y-6">
            {results && (
              <>
                <div className="card-premium rounded-2xl p-5 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">Investment Overview</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    <CompactMetric label="Property Price" value={formatCurrency(results.total_property_price)} subtitle="Purchase" positive={true} testId="public-metric-price" />
                    <CompactMetric label="Monthly Payment" value={formatCurrency(results.monthly_mortgage_payment)} subtitle="Mortgage" positive={false} testId="public-metric-mortgage" />
                    <CompactMetric label="Cash Flow" value={formatCurrency(Math.abs(results.monthly_cash_flow))} subtitle={results.monthly_cash_flow >= 0 ? 'Positive' : 'Negative'} positive={results.monthly_cash_flow >= 0} testId="public-metric-cashflow" />
                    <CompactMetric label="Coverage" value={results.years_to_cover_mortgage ? `${results.years_to_cover_mortgage.toFixed(1)}y` : 'N/A'} subtitle="Years to Cover" positive={!!results.years_to_cover_mortgage} testId="public-metric-coverage" />
                    <CompactMetric label="ROI" value={`${results.roi_percentage.toFixed(1)}%`} subtitle="Return" positive={results.roi_percentage > 0} testId="public-metric-roi" />
                    <CompactMetric label="Net Income" value={formatCurrency(results.monthly_net_income)} subtitle="After Tax" positive={results.monthly_net_income > 0} testId="public-metric-net" />
                    <CompactMetric label="Interest" value={formatCurrency(results.total_interest_paid)} subtitle="Total" positive={false} testId="public-metric-interest" />
                    <CompactMetric label="Break-Even" value={results.break_even_years ? `${results.break_even_years.toFixed(1)}y` : 'N/A'} subtitle="Time" positive={!!results.break_even_years} testId="public-metric-breakeven" />
                  </div>
                </div>

                <div className="card-premium rounded-2xl p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4 sm:mb-5">
                    <ChartLineUp size={20} weight="duotone" className="text-[#FF6B35]" />
                    <h3 className="text-base font-semibold text-white">Cash Flow Timeline</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={results.cash_flow_timeline}>
                      <defs>
                        <linearGradient id="colorCF" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a2556', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px' }} />
                      <Area type="monotone" dataKey="net" stroke="#FF6B35" strokeWidth={2.5} fillOpacity={1} fill="url(#colorCF)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="card-premium rounded-2xl p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-white mb-4 sm:mb-5">Payment Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={paymentBreakdownData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                          {paymentBreakdownData.map((entry, index) => (
                            <Cell key={`pie-${entry.name}`} fill={[COLORS.primary, COLORS.secondary, COLORS.danger][index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1a2556', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="card-premium rounded-2xl p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-white mb-4 sm:mb-5">Monthly Overview</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={[
                        { name: 'Rent', value: results.monthly_rent_income },
                        { name: 'Mortgage', value: results.monthly_mortgage_payment },
                        { name: 'Net', value: results.monthly_net_income }
                      ]}>
                        <XAxis dataKey="name" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1a2556', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px' }} />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                          <Cell fill={COLORS.primary} />
                          <Cell fill={COLORS.secondary} />
                          <Cell fill={COLORS.danger} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </main>

      <footer className="py-8">
        <p className="text-center text-slate-500 text-sm">&copy; 2026 Menelaus Holding. All rights reserved.</p>
      </footer>
    </div>
  );
};

const InputField = ({ label, value, onChange, step, testId }) => (
  <div>
    <label className="text-xs font-medium uppercase text-slate-400 mb-2 block tracking-wider">{label}</label>
    <input type="number" value={value} onChange={onChange} step={step} data-testid={testId}
      className="w-full bg-[#0B1437]/80 border border-white/10 rounded-xl h-11 px-4 text-white text-sm focus:outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] transition-all duration-200" />
  </div>
);

const CompactMetric = ({ label, value, subtitle, positive, testId }) => (
  <div className="p-3 sm:p-4 rounded-xl bg-[#1a2556]/60 backdrop-blur-xl border border-[#FF6B35]/20 hover:border-[#FF6B35]/40 transition-all duration-200" style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.1)' }} data-testid={testId}>
    <p className="text-xs font-medium uppercase text-slate-400 mb-1 sm:mb-1.5 tracking-wider truncate">{label}</p>
    <p className="text-lg sm:text-xl md:text-2xl font-mono font-bold text-white mb-0.5 sm:mb-1 tracking-tight break-all" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</p>
    <span className={`text-xs font-semibold truncate block ${positive ? 'text-[#FF6B35]' : 'text-[#ef4444]'}`}>{subtitle}</span>
  </div>
);

export default PublicRealEstateCalculator;
